import { useMemo, useState } from "react";
import type { RealOrAiConfig, RealOrAiRound, RealOrAiTile } from "../scenario/types";
import { tx } from "../lib/tx";

function TileMedia({
  clipUrl,
  imageUrl,
  name,
}: {
  clipUrl?: string;
  imageUrl?: string;
  name: string;
}) {
  const [clipFailed, setClipFailed] = useState(false);
  if (clipUrl && !clipFailed) {
    return (
      <video
        src={clipUrl}
        muted
        loop
        autoPlay
        playsInline
        onError={() => setClipFailed(true)}
      />
    );
  }
  if (imageUrl) return <img src={imageUrl} alt="" />;
  return <span>{name.split(" ").map((x) => x[0]).join("")}</span>;
}

function shuffleTiles(tiles: RealOrAiTile[], salt: number): RealOrAiTile[] {
  const next = tiles.slice();
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = (salt + i * 17) % (i + 1);
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

export function RealOrAi({ config, onDone }: { config: RealOrAiConfig; onDone: () => void }) {
  const rounds: RealOrAiRound[] = config.stages?.length
    ? config.stages
    : [{ headline: config.headline, prompt: config.prompt, tiles: config.tiles, revealBody: config.revealBody }];
  const [roundIndex, setRoundIndex] = useState(() => {
    try {
      const requested = Number(new URLSearchParams(window.location.search).get("realAiRound") ?? "0");
      return Number.isInteger(requested) && requested >= 0 && requested < rounds.length ? requested : 0;
    } catch {
      return 0;
    }
  });
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [revealed, setRevealed] = useState(false);
  const [confidence, setConfidence] = useState(50);
  const round = rounds[roundIndex];
  const tiles = useMemo(
    () => (roundIndex === 0 ? round.tiles : shuffleTiles(round.tiles, 29 + roundIndex * 11)),
    [round.tiles, roundIndex],
  );
  const lastRound = roundIndex >= rounds.length - 1;

  const toggle = (id: string) =>
    setSelected((old) => {
      const next = new Set(old);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const correct = tiles.filter((t) => (selected.has(t.id) ? t.mediaType === "real" : t.mediaType === "ai")).length;

  const nextRound = () => {
    setSelected(new Set());
    setRevealed(false);
    setConfidence(50);
    setRoundIndex((value) => value + 1);
  };

  return (
    <section className="real-or-ai">
      <header>
        <small>{round.kicker ?? (rounds.length > 1 ? tx("DEEPFAKE GAME · ROUND {v} OF {length}", { v: roundIndex + 1, length: rounds.length }) : tx("DEEPFAKE GAME"))}</small>
        <h2>{round.headline ?? config.headline ?? tx("Deepfake: Which people are real?")}</h2>
        <p>{round.prompt ?? config.prompt ?? tx("Select the people you believe are real recordings.")}</p>
      </header>
      <div className="rai-grid">
        {tiles.map((tile) => (
          <button key={tile.id} className={`${selected.has(tile.id) ? "selected" : ""} ${revealed ? tile.mediaType : ""}`} onClick={() => !revealed && toggle(tile.id)}>
            <div className="rai-media">
              <TileMedia clipUrl={tile.clipUrl} imageUrl={tile.imageUrl} name={tile.name} />
              <i>{selected.has(tile.id) ? tx("Selected as real") : tx("Select as real")}</i>
              {revealed && <b>{tile.mediaType === "real" ? tx("Real recording") : tx("AI generated")}</b>}
            </div>
            <strong>{tile.name}</strong><small>{tile.role}</small>
          </button>
        ))}
      </div>

      {!revealed ? (
        <footer className="rai-controls">
          <label>{tx("How confident are you?")}<input type="range" min="0" max="100" value={confidence} onChange={(e) => setConfidence(Number(e.target.value))} /><strong>{confidence}%</strong></label>
          <button className="ht-primary" onClick={() => setRevealed(true)}>{tx("Reveal the answer")}</button>
        </footer>
      ) : (
        <div className="rai-reveal">
          <div><small>{tx("YOUR RESULT")}</small><strong>{correct}/{tiles.length}</strong><span>{tx("correct classifications")}</span></div>
          <p>{round.revealBody ?? config.revealBody ?? tx("If your security process depends on recognizing a deepfake, eventually you will lose.")}</p>
          <blockquote>{tx("Verify identity through an independent trusted channel.")}</blockquote>
          {lastRound ? (
            <button className="ht-primary" onClick={onDone}>{config.continueLabel ?? tx("Continue")}</button>
          ) : (
            <button className="ht-primary" onClick={nextRound}>{round.nextLabel ?? tx("Next round")}</button>
          )}
        </div>
      )}
    </section>
  );
}
